import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar, MapPin, Globe, Users, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Button from './Button';
import UserName from './UserName';
import type { CommunityEvent } from '../types';
import supabase from "../lib/supabase";
import { Link } from 'react-router-dom';
import Avatar from './Avatar';


interface EventCardProps {
  event: CommunityEvent;
  onUpdate: () => void;
}

interface Attendee {
  user: {
    id: string;
    full_name: string;
    display_name: string | null;
    verified: boolean;
    avatar_url: string | null;
  };
  status: 'yes' | 'no' | 'maybe';
}

export default function EventCard({ event, onUpdate }: EventCardProps) {
  const { user } = useAuthStore();
  const [rsvpStatus, setRsvpStatus] = useState<'yes' | 'no' | 'maybe' | null>(null);
  const [rsvpCounts, setRsvpCounts] = useState({
    yes: 0,
    no: 0,
    maybe: 0
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAttendees, setShowAttendees] = useState(false);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoadingAttendees, setIsLoadingAttendees] = useState(false);
  const [hideTimeout, setHideTimeout] = useState<NodeJS.Timeout | null>(null);
  const attendeesRef = React.useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(event.title);
  const [editDescription, setEditDescription] = useState(event.description);
  const [editLocation, setEditLocation] = useState(event.location || '');
  const [editMeetingUrl, setEditMeetingUrl] = useState(event.meeting_url || '');
  const [editStartTime, setEditStartTime] = useState(event.start_time);
  const [editEndTime, setEditEndTime] = useState(event.end_time);
  const [editIsOnline, setEditIsOnline] = useState(event.is_online);

  const canModify = user && user.id === event.creator.id;

  const fetchRsvpData = React.useCallback(async () => {
    try {
      if (user) {
        const { data: userRsvp } = await supabase
          .from('event_rsvps')
          .select('status')
          .eq('event_id', event.id)
          .eq('user_id', user.id)
          .maybeSingle();

        setRsvpStatus(userRsvp?.status || null);
      }

      const { data: counts, error: countsError } = await supabase
        .from('event_rsvps')
        .select('status')
        .eq('event_id', event.id);

      if (countsError) throw countsError;

      const newCounts = counts?.reduce((acc, rsvp) => {
        acc[rsvp.status] = (acc[rsvp.status] || 0) + 1;
        return acc;
      }, { yes: 0, no: 0, maybe: 0 });

      setRsvpCounts(newCounts || { yes: 0, no: 0, maybe: 0 });
    } catch (error) {
      console.error('Error fetching RSVP data:', error);
    }
  }, [event.id, user]);

  useEffect(() => {
    fetchRsvpData();
  }, [fetchRsvpData]);

  const fetchAttendees = React.useCallback(async () => {
    try {
      setIsLoadingAttendees(true);
      const { data, error } = await supabase
        .from('event_rsvps')
        .select(`
          status,
          user:user_profiles(
            id,
            full_name,
            display_name,
            verified,
            avatar_url
          )
        `)
        .eq('event_id', event.id)
        .eq('status', 'yes')
        .order('status', { ascending: true });

      if (error) throw error;
      setAttendees(data || []);
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setIsLoadingAttendees(false);
    }
  }, [event.id]);

  const handleMouseEnter = () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setShowAttendees(true);
    fetchAttendees();
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowAttendees(false);
    }, 300);
    setHideTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (attendeesRef.current && !attendeesRef.current.contains(event.target as Node)) {
        setShowAttendees(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRsvp = async (status: 'yes' | 'no' | 'maybe') => {
    if (!user) return;

    try {
      setIsUpdating(true);

      if (rsvpStatus) {
        const { error } = await supabase
          .from('event_rsvps')
          .update({ status })
          .eq('event_id', event.id)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('event_rsvps')
          .insert({
            event_id: event.id,
            user_id: user.id,
            status
          });

        if (error) throw error;
      }

      setRsvpStatus(status);
      await fetchRsvpData();
      onUpdate();
    } catch (error) {
      console.error('Error updating RSVP:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEdit = async () => {
    try {
      setIsUpdating(true);
      const { error } = await supabase
        .from('community_events')
        .update({
          title: editTitle,
          description: editDescription,
          location: editLocation || null,
          meeting_url: editMeetingUrl || null,
          start_time: editStartTime,
          end_time: editEndTime,
          is_online: editIsOnline,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id)
        .eq('creator_id', user?.id);

      if (error) throw error;

      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating event:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      setIsUpdating(true);
      const { error } = await supabase
        .from('community_events')
        .delete()
        .eq('id', event.id)
        .eq('creator_id', user?.id);

      if (error) throw error;

      onUpdate();
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-surface-light rounded-xl p-4 group hover:bg-neutral">
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-0">
          <div className="flex items-center gap-4">
            <h3 className="text-xl font-semibold text-primary group-hover:text-surface-light">
              {event.title}
            </h3>
          </div>

          {canModify && (
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-neutral group-hover:text-surface-light transition-colors"
                title="Edit event"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 text-neutral group-hover:text-surface-light transition-colors"
                title="Delete event"
                disabled={isUpdating}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4 mt-4">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none placeholder:text-primary"
              placeholder="Event Title"
            />
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none placeholder:text-primary"
              placeholder="Event Description"
              rows={3}
            />

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium font-dillan text-neutral group-hover:text-surface-light mb-1">Start Time</label>
                <input
                  type="datetime-local"
                  value={new Date(editStartTime).toISOString().slice(0, 16)}
                  onChange={(e) => setEditStartTime(new Date(e.target.value).toISOString())}
                  className="w-[140px] sm:w-full px-2 py-1 text-xs rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-neutral group-hover:text-surface-light mb-1">End Time</label>
                <input
                  type="datetime-local"
                  value={new Date(editStartTime).toISOString().slice(0, 16)}
                  onChange={(e) => setEditEndTime(new Date(e.target.value).toISOString())}
                  className="w-[140px] sm:w-full px-2 py-1 text-xs rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={editIsOnline}
                onChange={(e) => setEditIsOnline(e.target.checked)}
                className="mt-1 w-4 h-4 pl-2 border-2 border-neutral rounded-full 
      appearance-none transition-all 
      checked:bg-neutral checked:border-neutral
      group-hover:border-surface-light
      group-hover:checked:bg-surface-light group-hover:checked:border-surface-light"
              />
              <label className="text-sm font-medium text-neutral group-hover:text-surface-light mt-1">Online Event</label>
            </div>
            {editIsOnline ? (
              <input
                type="url"
                value={editMeetingUrl}
                onChange={(e) => setEditMeetingUrl(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none placeholder:text-primary"
                placeholder="Meeting URL"
              />
            ) : (
              <div className="relative w-full mb-4">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary" />
                <input
                  type="text"
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                  className="w-full pl-10 pr-2 py-2 rounded-lg bg-primary-dark text-primary focus:ring-0 focus:outline-none placeholder:text-primary"
                  placeholder="Location"
                />
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                size="sm"
                className="w-16 h-16 rounded-full text-s bg-secondary-light  border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
                onClick={() => {
                  setIsEditing(false);
                  setEditTitle(event.title);
                  setEditDescription(event.description);
                  setEditLocation(event.location || '');
                  setEditMeetingUrl(event.meeting_url || '');
                  setEditStartTime(event.start_time);
                  setEditEndTime(event.end_time);
                  setEditIsOnline(event.is_online);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="w-16 h-16 rounded-full text-s bg-secondary-light  border-0 border-transparent text-neutral hover:bg-neutral hover:text-surface-light hover:border-neutral"
                onClick={handleEdit}
                disabled={isUpdating}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-sm text-neutral mb-2 group-hover:text-surface-light">
              {event.community && (
                <Link 
                  to={`/communities/${event.community.id}`}
                  className="text-neutral text-m group-hover:text-primary transition-colors italic"
                >
                  {event.community.name}
                </Link>
              )}
            </div>

            <div className="prose prose-sm max-w-none text-primary group-hover:text-surface-light">
              {event.description}
            </div>
            <div className="mb-2">
              <UserName
                name={event.creator.display_name || event.creator.full_name}
                verified={event.creator.verified}
                size="sm"
                userId={event.creator.id}
                className="text-neutral group-hover:text-primary"
              />
            </div>

            <div className="flex flex-col gap-2 mb-8">
              <div className="flex items-center text-neutral group-hover:text-surface-light">
                <Calendar className="w-4 h-4 mr-1.5" />
                {format(new Date(event.start_time), 'PP HH:mm')}
              </div>
              {event.is_online ? (
                <div className="flex items-center text-neutral group-hover:text-surface-light">
                  <Globe className="w-4 h-4 mr-1.5" />
                  {event.meeting_url ? (
                    <a 
                      href={event.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral group-hover:text-surface-light hover:underline"
                    >
                      Join Online Meeting
                    </a>
                  ) : (
                    'Online'
                  )}
                </div>
              ) : event.location && (
                <div className="flex items-center text-neutral group-hover:text-surface-light">
                  <MapPin className="w-4 h-4 mr-1.5" />
                  {event.location}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              {user && (
                <div className="flex gap-4">
                  <Button
                    variant={rsvpStatus === 'yes' ? 'primary' : 'outline'}
                    size="sm"
                    className={`w-16 h-16 rounded-full ${rsvpStatus === 'yes' ? 'bg-secondary-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-neutral hover:border-secondary-light' : 'bg-surface-light border-2 border-surface text-neutral hover:bg-secondary-light hover:text-neutral hover:border-secondary-light'}`}
                    onClick={() => handleRsvp('yes')}
                    disabled={isUpdating}
                  >
                    Going
                  </Button>
                  <Button
                    variant={rsvpStatus === 'maybe' ? 'primary' : 'outline'}
                    size="sm"
                    className={`w-16 h-16 rounded-full ${rsvpStatus === 'maybe' ? 'bg-secondary-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-neutral hover:border-secondary-light' : 'bg-surface-light border-2 border-surface text-neutral hover:bg-secondary-light hover:text-neutral hover:border-secondary-light'}`}
                    onClick={() => handleRsvp('maybe')}
                    disabled={isUpdating}
                  >
                    Maybe
                  </Button>
                  <Button
                    variant={rsvpStatus === 'no' ? 'primary' : 'outline'}
                    size="sm"
                    className={`w-16 h-16 rounded-full ${rsvpStatus === 'no' ? 'bg-secondary-light border-0 border-transparent text-neutral hover:bg-secondary-light hover:text-neutral hover:border-secondary-light' : 'bg-surface-light border-2 border-surface text-neutral hover:bg-secondary-light hover:text-neutral hover:border-secondary-light'}`}
                    onClick={() => handleRsvp('no')}
                    disabled={isUpdating}
                  >
                    No
                  </Button>
                </div>
              )}

              <div className="relative">
                <button
                  className="flex items-center text-sm text-neutral group-hover:text-surface-light transition-colors"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="flex items-center mt-2">
                    <Users className="w-4 h-4 mr-0.5" />
                    <span>{rsvpCounts.yes} going</span>
                  </div>
                </button>

                {showAttendees && (
                  <div 
                    ref={attendeesRef}
                    className="absolute right-0 top-full w-36 max-h-60 overflow-y-auto bg-neutral rounded-xl shadow-elevation-2 p-2 mt-8"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {isLoadingAttendees ? (
                      <div className="text-center py-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-surface mx-auto"></div>
                        <p className="text-sm text-surface mt-2">Loading attendees...</p>
                      </div>
                    ) : attendees.length > 0 ? (
                      <div className="space-y-3">
                        {attendees.map((attendee) => (
                          <Link 
                            key={attendee.user.id}
                            to={`/users/${attendee.user.id}`}
                            className="flex items-center space-x-2 text-surface-light hover:bg-surface hover:text-neutral p-2 rounded-lg transition-colors"
                          >
                            <Avatar
                              src={attendee.user.avatar_url || `https://ui-avatars.com/api/?name=${attendee.user.display_name || attendee.user.full_name}`}
                              alt={attendee.user.display_name || attendee.user.full_name}
                              size="sm"
                            />
                          <span>{attendee.user.display_name || attendee.user.full_name}</span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-surface text-center py-2">
                        No responses yet
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}